/**
 * Hooks for regulatory packages list and download.
 *
 * @taskId T658
 * @taskId T659
 * @see functional/Cycles/cycle-5/tasks.md HU-99-FE
 */

import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import {
  downloadRegulatoryPackage,
  getRegulatoryPackages,
  type RegulatoryPackage,
  type RegulatoryPackagesPage,
  type RegulatoryPackagesParams,
} from '../api/regulatory-packages.api';

const THIRTY_SECONDS = 1000 * 30;

export const regulatoryPackagesQueryKeys = {
  all: ['regulatory-packages'] as const,
  list: (params: RegulatoryPackagesParams) =>
    [
      ...regulatoryPackagesQueryKeys.all,
      'list',
      params.page,
      params.size,
      params.status ?? 'ALL',
    ] as const,
};

function buildFilename(pkg: RegulatoryPackage): string {
  const period = pkg.periodYyyymm ?? pkg.period ?? 'sin-periodo';
  const type = pkg.reportType?.toLowerCase() ?? 'paquete';
  return `paquete_regulatorio_${type}_${period}_${pkg.id}.zip`;
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function useRegulatoryPackages(params: RegulatoryPackagesParams) {
  return useQuery<RegulatoryPackagesPage, Error>({
    queryKey: regulatoryPackagesQueryKeys.list(params),
    queryFn: () => getRegulatoryPackages(params),
    placeholderData: keepPreviousData,
    staleTime: THIRTY_SECONDS,
    refetchOnWindowFocus: false,
  });
}

export function useRegulatoryPackageDownload() {
  return useMutation({
    mutationFn: (pkg: RegulatoryPackage) => downloadRegulatoryPackage(pkg.id),
    onSuccess: (blob, pkg) => {
      triggerBlobDownload(blob, buildFilename(pkg));
    },
  });
}